import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, ArrowLeft, Upload, Image as ImageIcon, Video, Music, FileText, Trash2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconForKind = (kind, mime) => {
  if (kind === 'images' || mime?.startsWith('image/')) return ImageIcon;
  if (kind === 'videos' || mime?.startsWith('video/')) return Video;
  if (kind === 'audio' || mime?.startsWith('audio/')) return Music;
  return FileText;
};

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MediaLibraryPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchMedia(); }, []);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/media/my`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch media');
    } finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${files.length} file(s) uploaded`);
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.filename}?`)) return;
    try {
      if (item.type === 'media') {
        await axios.delete(`${API}/media/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.delete(`${API}/documents/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('Deleted');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button data-testid="back-btn" onClick={() => navigate(-1)} variant="outline" size="sm" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex items-center gap-2">
              <Home className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold tracking-tight text-primary">PrimePocket</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">Media Library</h1>
            <p className="text-muted-foreground mt-2">Your images, videos, audio, and documents ({items.length} items)</p>
          </div>
          <div>
            <input ref={fileInputRef} type="file" data-testid="media-file-input"
              accept="image/*,video/*,audio/*"
              multiple onChange={handleUpload} className="hidden" />
            <Button data-testid="media-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
              <Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading...' : 'Upload Media'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">Loading...</div>
        ) : items.length === 0 ? (
          <Card className="p-20 text-center border-2 border-dashed">
            <ImageIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">No media yet</h3>
            <p className="text-muted-foreground">Upload images, videos, or audio files to get started</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((item, idx) => {
              const Icon = iconForKind(item.kind, item.mime_type);
              const isImage = item.mime_type?.startsWith('image/');
              const fullUrl = item.url.startsWith('http') ? item.url : `${BACKEND_URL}${item.url}`;
              return (
                <Card key={item.id} className="p-4 border border-border" data-testid={`media-${idx}`}>
                  <div className="aspect-square rounded-lg overflow-hidden bg-muted mb-3 flex items-center justify-center">
                    {isImage ? (
                      <img src={fullUrl} alt={item.filename} className="w-full h-full object-cover" />
                    ) : (
                      <Icon className="w-16 h-16 text-muted-foreground" />
                    )}
                  </div>
                  <div className="text-sm font-medium truncate" title={item.filename}>{item.filename}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center justify-between">
                    <span>{formatSize(item.size)}</span>
                    <span>{item.kind}</span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" size="sm" className="w-full rounded-full">
                        <ExternalLink className="w-3 h-3 mr-1" />Open
                      </Button>
                    </a>
                    <Button data-testid={`delete-media-${idx}-btn`} onClick={() => handleDelete(item)} variant="outline" size="sm" className="rounded-full text-destructive border-destructive">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
